<template>
    <Layout :localeData="localeData">
        <main
            :class="$style.main"
            data-interface-section="listing"
        >
            <CategoryHeader :category="categoryHeaderInfo" />
            <div :class="$style.columns">
                <div :class="$style.primary_content">
                    <div
                        :id="`page-${currentPageIndex}`"
                        :class="$style.grid"
                        :data-page-number="currentPageIndex"
                    >
                        <template v-if="games.length > 0 && categoryInfo">
                            <GameCard
                                v-for="(game, cardIndex) in games"
                                :key="game.hru"
                                :data-interface-target="`game-number-${cardIndex + 1}`"
                                :game="game"
                                :ctrContext="generalCtrContexts[cardIndex]"
                                responsive
                                withBadges
                                :targetLink="undefined"
                                withScaleOnHover
                                type="rectangle"
                                :class="$style.card_override"
                                :autoplayVideoInViewport="cardIndex === 0"
                                v-bind="
                                    cardIndex < 3
                                        ? {
                                              preload: cardIndex === 0,
                                              lazy: cardIndex !== 0,
                                          }
                                        : {}
                                "
                            />
                        </template>
                        <template v-else>
                            <GameCard
                                v-for="(_, cardIndex) in placeholderPage"
                                :key="`placeholder-${cardIndex}`"
                                :game="undefined"
                                responsive
                                withBadges
                                withScaleOnHover
                                type="rectangle"
                                :class="$style.card_override"
                            />
                        </template>
                        <div
                            v-if="showTrending"
                            :class="$style.trending"
                            data-interface-section="trending-embed"
                        >
                            <ShelfTitle
                                :title="trendingCategory.title"
                                :link="trendingCategory.location"
                                categoryId="trending_now"
                            />
                            <GeneralShelf
                                :key="currentPageIndex"
                                :games="trendingList"
                                :link="trendingCategory.location"
                                :gameLinkTarget="undefined"
                                :ctrContexts="trendingCtrContexts"
                                embedded
                                :categoryName="trendingCategory.title"
                            />
                        </div>
                    </div>

                    <PageToggle
                        v-if="category?.totalPages && category.totalPages > 1"
                        :currentPage="currentPageIndex"
                        :totalPages="category.totalPages"
                        :getPageLink="getPageLink"
                    />

                    <SeoText
                        v-if="!appStore.isAppEnv && categoryInfo?.seoText && currentPageIndex === 1"
                        :content="categoryInfo?.seoText || ''"
                        :pictureData="seoPictureData"
                        loading="eager"
                        fetchpriority="high"
                        :class="$style.seo_override"
                    />

                    <PromoCategories :class="$style.promo_override" />

                    <Breadcrumbs
                        v-if="!appStore.isAppEnv"
                        :breadcrumbs="pathCategories"
                    />
                </div>
                <RightColumn
                    :key="$route.fullPath"
                    :class="$style.right_override"
                />
            </div>
        </main>
    </Layout>
</template>
<script setup lang="ts">
import { type ReactiveHead, useHead } from '@unhead/vue'
import { computed, onMounted, onServerPrefetch, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Breadcrumbs from '@/components/Breadcrumbs.vue'
import GameCard from '@/components/GameCard/GameCard.vue'
import GeneralShelf from '@/components/GamesShelf/GeneralShelf.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import PageToggle from '@/components/PageToggle/PageToggle.vue'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import RightColumn from '@/components/RightColumn.vue'
import SeoText from '@/components/SeoText/SeoText.vue'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { useUrl } from '@/composables/url-helpers'
import { useCtrListContexts } from '@/composables/use-ctr-context'
import { usePageQuery } from '@/composables/use-page-query'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import Layout from '@/layouts/Layout.vue'
import { useTrendingData } from '@/query/use-trending-data'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY, pageQuery } from '@/router/query-params'
import { useAdsStore } from '@/store/ads-store'
import { useAppStore } from '@/store/app-store'
import { type APIRedirect, type CategoryType, RedirectReason } from '@/types'
import { useSetAdTargetingOnMount } from '@/utils/vue-hooks/use-set-ad-targeting-on-mount'

import CategoryHeader from './components/CategoryHeader.vue'
import { getCategoryPageTitles } from './get-category-page-titles'
import { useCategoryPageData } from './use-category-page-data'

const TOP_GAMES_COUNT = 20

const appStore = useAppStore()
const adsStore = useAdsStore()
const $route = useRoute()
const router = useRouter()

function getPageLink(page: number) {
    return {
        name: pageType.value,
        query: pageQuery(page),
    }
}

const tr = useTr()

const categoryHru = computed(() => $route.params.hru as string)
const currentPageIndex = usePageQuery()
const pinnedGame = computed(() => $route.query[SSR_QUERY.PINNED_GAME] as string | undefined)

const pageType = computed<CategoryType>(() => {
    switch ($route.name) {
        case ROUTE_NAMES.TAG:
            return 'tag'
        case ROUTE_NAMES.SERIES:
            return 'series'
        case ROUTE_NAMES.DEVELOPER:
            return 'developer'
        default:
            return 'category'
    }
})

const { games, pageSize, category, categoryInfo, categoryRedirect, fetchStatus, hasError, preloadCategoryPageData } =
    useCategoryPageData({
        categoryName: categoryHru,
        pageType,
        page: currentPageIndex,
        pinnedGame,
    })

const generalCtrContexts = useCtrListContexts(
    () => games.value,
    () => ({ placement: 'general', dimension1: categoryInfo.value?.id }),
)
const placeholderPage = computed(() => Array(pageSize.value).fill(null))
const { Organization, WebSite, WebSiteId, getFullLink, getInLanguage } = useSchemaOrg()
const { getCategoryRoute } = useUrl()

const additionalTrendingKeys = computed(() => [categoryHru.value])
// always load trending data, but hide it later on certain categories
const {
    currentTrending: trendingCategory,
    unzippedTrendingWithGames: trendingGames,
    hasError: trendingHasError,
    preloadTrendingData,
} = useTrendingData({
    // pass additional key to make shuffle uniq per page
    additionalKeys: additionalTrendingKeys,
})
const trendingList = computed(() => trendingGames.value.slice(0, 9))
const trendingCtrContexts = useCtrListContexts(
    () => trendingList.value,
    () => ({ placement: 'trending_now', dimension1: categoryInfo.value?.id }),
)

const showTrending = computed(() => {
    // DEV-4662: Temporarily disable trending for organic traffic
    if (!appStore.contentScope) {
        return false
    }
    if (pageType.value === 'developer') {
        return false
    }
    if (categoryInfo.value) {
        return !['trending_now'].includes(categoryInfo.value.id) && trendingGames.value.length > 0
    }
    return false
})

const pathCategories = computed(() => {
    const catPath = categoryInfo.value?.pathCategories || []
    const path = catPath.map((p, index) => ({
        ...p,
        disabled: index === catPath.length - 1,
    }))
    if (path) {
        return [
            {
                title: tr('categoryPage.main_title'),
                href: router.resolve({ name: ROUTE_NAMES.MAIN }).fullPath,
            },
            ...path,
        ]
    }
    return []
})

const categoryPageTitles = computed(() =>
    getCategoryPageTitles({
        categoryInfo: categoryInfo.value,
        pageType: pageType.value,
        tr,
    }),
)
const headingTitle = computed(() => categoryPageTitles.value.headingTitle)
const templateTitle = computed(() => categoryPageTitles.value.templateTitle)

const categoryHeaderInfo = computed(() =>
    categoryInfo.value
        ? {
              ...categoryInfo.value,
              fullTitle: headingTitle.value,
          }
        : undefined,
)

const seoPictureData = computed(() => {
    return categoryInfo.value?.seoPicture
        ? {
              src: categoryInfo.value.seoPicture,
              alt: headingTitle.value,
          }
        : undefined
})

const defaultMetaTitle = computed(() => categoryPageTitles.value.defaultMetaTitle)
const defaultMetaDescription = computed(() => categoryPageTitles.value.defaultMetaDescription)

const metaTitle = computed(() => categoryInfo.value?.metaTitle || defaultMetaTitle.value)

const metaDescription = computed(() => categoryInfo.value?.metaDescription || defaultMetaDescription.value)

const pageStr = computed(() =>
    currentPageIndex.value > 1 ? ` | ${tr('categoryPage.page', { page: String(currentPageIndex.value) })}` : '',
)

const breadcrumbsSchema = computed(() => {
    if (!pathCategories.value.length) {
        return {}
    }

    const breadcrumbItems = pathCategories.value.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
            '@type': 'Thing',
            '@id': getFullLink(item.href),
            name: item.title,
        },
    }))

    return {
        breadcrumb: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
        },
    }
})

const mainEntity = computed(() => {
    const currentPageGames = games.value.slice(0, TOP_GAMES_COUNT)

    if (!currentPageGames.length || currentPageIndex.value > 1) {
        return {}
    }

    const gameItems = currentPageGames.map((game, index) => ({
        '@type': 'ListItem',
        position: 1 + index,
        item: {
            '@type': ['VideoGame', 'WebApplication'],
            name: game.title,
            url: getFullLink({ name: ROUTE_NAMES.GAME_PAGE, params: { game: game.hru } }),
            operatingSystem: ['Windows', 'Chrome OS', 'Linux', 'MacOS', 'Android', 'iOS'],
            applicationCategory: 'GameApplication',
            ...(game.preview ? { image: { '@type': 'ImageObject', url: game.preview } } : {}),
        },
    }))

    return {
        mainEntity: {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: templateTitle.value,
            numberOfItems: currentPageGames.length,
            itemListElement: gameItems,
        },
    }
})
const organizationSchema = computed(() => {
    if (!categoryInfo.value) {
        return {}
    }

    const currentRouteWithPagination = getFullLink({
        name: pageType.value,
        query: pageQuery(currentPageIndex.value),
    })
    const lang = getInLanguage()
    const categoryPicture = categoryInfo.value.seoPicture
        ? {
              '@type': 'ImageObject',
              url: categoryInfo.value.seoPicture,
              contentUrl: categoryInfo.value.seoPicture,
              width: 1200,
              height: 1200,
              caption: tr('categoryPage.seo_caption', { title: templateTitle.value }),
              name: `${templateTitle.value} Cover Image`,
              inLanguage: lang,
          }
        : undefined
    const primaryCategoryPicture = categoryPicture ? { ...categoryPicture, representativeOfPage: true } : undefined

    return {
        '@context': 'https://schema.org',
        '@graph': [
            Organization.value,
            WebSite.value,
            {
                '@type': 'CollectionPage',
                '@id': `${currentRouteWithPagination}#CollectionPage`,
                url: currentRouteWithPagination,
                name: `${metaTitle.value}${pageStr.value}`,
                description: `${metaDescription.value}${pageStr.value}`,
                inLanguage: lang,
                ...(primaryCategoryPicture ? { primaryImageOfPage: primaryCategoryPicture } : {}),
                ...(categoryPicture ? { image: categoryPicture } : {}),
                ...(categoryInfo.value.seoPicture ? { thumbnailUrl: categoryInfo.value.seoPicture } : {}),
                isPartOf: {
                    '@id': WebSiteId.value,
                },
                potentialAction: {
                    '@type': 'SearchAction',
                    target: {
                        '@type': 'EntryPoint',
                        urlTemplate: getFullLink({
                            name: ROUTE_NAMES.SEARCH,
                            query: { [SSR_QUERY.SEARCH_QUERY]: '{search_term_string}' },
                        }),
                    },
                    'query-input': 'required name=search_term_string',
                },
                ...breadcrumbsSchema.value,
                ...mainEntity.value,
            },
        ],
    }
})

const { getCanonicalAndAlternates, getLocalizedUrls } = useLocalizedRouters()

const pageMeta = computed(() => {
    if (!category.value || !category.value.id || !categoryInfo.value) {
        return {}
    }

    const links: ReactiveHead['link'] = [
        ...getCanonicalAndAlternates({
            mapRoute(route, locale) {
                const localizedHru = categoryInfo.value?.localizations?.find((l) => l.lang === locale)?.hru
                if (!localizedHru) {
                    return route
                }
                return {
                    ...route,
                    params: {
                        ...route.params,
                        hru: localizedHru,
                    },
                    query: {
                        [SSR_QUERY.PAGE]: route.query?.[SSR_QUERY.PAGE],
                    },
                }
            },
        }),
    ]

    if (currentPageIndex.value > 1) {
        links.push({
            rel: 'prev',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: pageQuery(currentPageIndex.value - 1),
            }).href,
        })
    }
    if (currentPageIndex.value < category.value.totalPages) {
        links.push({
            rel: 'next',
            href: router.resolve({
                ...getCategoryRoute({ hru: categoryHru.value, type: category.value.categoryType }),
                query: pageQuery(currentPageIndex.value + 1),
            }).href,
        })
    }

    return {
        title: () => `${metaTitle.value}${pageStr.value}`,
        script: [
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema.value,
            },
        ],
        meta: [
            {
                name: 'title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                name: 'description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
            {
                property: 'og:title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                property: 'og:description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
            ...(categoryInfo.value?.seoPicture
                ? [
                      { property: 'og:image', content: categoryInfo.value.seoPicture },
                      {
                          property: 'og:image:alt',
                          content: tr('categoryPage.seo_caption', { title: templateTitle.value }),
                      },
                      { name: 'twitter:image', content: categoryInfo.value.seoPicture },
                      {
                          name: 'twitter:image:alt',
                          content: tr('categoryPage.seo_caption', { title: templateTitle.value }),
                      },
                  ]
                : []),
            {
                name: 'twitter:title',
                content: `${metaTitle.value}${pageStr.value}`,
            },
            {
                name: 'twitter:description',
                content: `${metaDescription.value}${pageStr.value}`,
            },
        ],
        link: links,
    }
})

const localeData = computed(() => {
    return {
        languageLinks: getLocalizedUrls({
            mapRoute(route, locale) {
                const localizedHru = categoryInfo.value?.localizations?.find((l) => l.lang === locale)?.hru

                return localizedHru
                    ? {
                          ...route,
                          params: {
                              ...route.params,
                              hru: localizedHru,
                          },
                          query: {
                              [SSR_QUERY.PAGE]: route.query?.[SSR_QUERY.PAGE],
                          },
                      }
                    : route
            },
        }),
    }
})

useHead(pageMeta)

watch(
    () => categoryInfo.value?.id,
    () => {
        adsStore.updateTargeting({ content_category: categoryInfo.value?.id || '' })
    },
)

// TODO deduplicate with Game.vue
function processRedirectResponse({ redirectTarget, status: statusCode }: APIRedirect) {
    let redirectURL = ''
    if (redirectTarget.type === 'game') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.GAME_PAGE,
            params: { game: redirectTarget.game_hru },
            query: router.currentRoute.value.query,
        }).href
    } else if (redirectTarget.type === 'category') {
        redirectURL = router.resolve({
            ...getCategoryRoute({ hru: redirectTarget.cat_hru, type: redirectTarget.cat_type }),
            query:
                redirectTarget.reason !== RedirectReason.PAGE_OUT_OF_BOUNDS
                    ? router.currentRoute.value.query
                    : undefined,
        }).href
    } else if (redirectTarget.type === 'main-page') {
        redirectURL = router.resolve({
            name: ROUTE_NAMES.MAIN,
            query: router.currentRoute.value.query,
        }).href
    }

    appStore.setHeaders({
        'X-BFF-Redirect-Mechanism': redirectTarget.mechanism,
        'X-BFF-Redirect-Reason': redirectTarget.reason,
    })
    appStore.setPageStatus(statusCode)
    appStore.setTargetURL(redirectURL)
}

function processResponse() {
    if (categoryRedirect.value) {
        processRedirectResponse(categoryRedirect.value)
        return
    }

    if (hasError.value) {
        appStore.setPageStatus(500)
        return
    }

    if (fetchStatus.value) {
        appStore.setPageStatus(fetchStatus.value)
        return
    }

    if (games.value.length === 0) {
        appStore.setPageStatus(404)
    }
}

onServerPrefetch(async () => {
    await Promise.all([preloadCategoryPageData(), preloadTrendingData()])
    processResponse()
    if (appStore.pageStatus === 200 && trendingHasError.value) {
        appStore.setPageStatus(500)
    }
})

useSetAdTargetingOnMount({
    content_category: pageType.value === 'category' ? categoryInfo.value?.id || '' : '',
})

onMounted(async () => {
    await Promise.all([preloadCategoryPageData(), preloadTrendingData()])
    processResponse()
})
</script>
<style module>
.main {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.columns {
    display: block;
}

.primary_content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
}

.seo_override {
    border-radius: 24px;
    background: var(--carbon-300);
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-self: stretch;
    width: unset;
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    column-gap: 8px;
    row-gap: 12px;
    position: relative;
}

.card_override:nth-child(-n + 1) {
    grid-column: span 2;
    grid-row: span 2;
}

.trending {
    grid-column-start: 1;
    grid-column-end: -1;
    grid-row-start: 5;
    background: var(--experimental-purple-graphit);
    border-radius: 16px;
    padding: 16px 12px;
    margin: 12px 0;
}

.right_override {
    display: none;
}

@media (--desktop-small-plus) {
    .main,
    .primary_content {
        gap: 32px;
    }
    .seo_override {
        padding: 32px;
    }

    .promo_override {
        padding: 8px 0 8px 0;
    }
}

@media (width >= 640px) {
    .grid {
        grid-template-columns: repeat(12, 1fr);
    }

    .card_override {
        grid-column: span 3;
    }

    .card_override:nth-child(-n + 3) {
        grid-column: span 4;
        grid-row: span 2;
    }

    .trending {
        grid-row-start: 4;
    }
}

@media (width >= 1400px) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }

    .card_override {
        grid-column: span 1;
    }

    .card_override:nth-child(-n + 3) {
        grid-column: span 2;
        grid-row: span 2;
    }
}

@media (--right-column-fits) {
    .columns {
        display: grid;
        grid-template-columns: 1fr var(--global-right-pannel-width);
        gap: 12px;
    }

    .right_override {
        display: block;
        flex-shrink: 0;
    }
}
</style>
